import React, { useEffect, useMemo, useRef, useState } from "react";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { useTranslation } from "../langs/useTranslation";
import { CircularProgress, Divider } from "@material-ui/core";
import { IconWindows, IconAndroid } from "../components/Icon";
import nubisIcon from "../assets/images/nubis.svg";
import {
  checkEmailVerifyCode,
  sendEmailVerifyCode
} from "../apis/apiCollection";
import { toast } from "@/helpers/toast";
import { authLogout } from "../apis/logoutAPI";
import Cookies from "js-cookie";
import { getSiteMainDomain } from "@/helpers/utils";
import { APP_ENV } from "@/APP_ENV";

const useStyles = makeStyles(theme => ({
  container: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    background: "linear-gradient(180deg, #F4FDFF 0%, #F7F9F9 100%)"
  },
  center: {
    marginTop: theme.spacing(17)
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
    boxShadow: "0px 4px 16px 0px rgba(117,118,126,0.15)",
    borderRadius: "20px",
    backgroundColor: "#fff"
  },
  logo: {
    width: 100,
    marginBottom: theme.spacing(3)
  },
  link: {
    display: "inline-block",
    textAlign: "center",
    color: `${theme.palette.primary.main} !important`
  },
  icon: {
    display: "inline-flex",
    backgroundColor: theme.palette.primary.opacity,
    color: theme.palette.primary.main,
    fontSize: 30
  },
  footer: {
    marginTop: theme.spacing(6)
  },
  footerSplit: {
    width: "60%",
    position: "relative",
    textAlign: "center"
  },
  footerSplitText: {
    display: "inline-block",
    backgroundColor: "#F7F9F9",
    padding: theme.spacing(1),
    transform: "translateY(-50%)"
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(2)
  },
  submit: {
    margin: theme.spacing(3, 0, 2)
  },
  regCode: {
    marginTop: theme.spacing(0.5)
  },
  resend: {
    color: theme.palette.primary.main,
    cursor: "pointer"
  }
}));

const countDuration = 61;

export default function SignIn() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [count, setCount] = useState(countDuration);
  const timer = useRef(null);

  const [code, setCode] = useState("");
  const [isError, setIsError] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const ticket = useMemo(() => {
    const params = new URL(document.location).searchParams;
    return params.get("ticket");
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading) return;
    if (!code.trim()) {
      setIsError(true);
      return;
    }
    try {
      setLoading(true);
      const res = await checkEmailVerifyCode({ ticket, code });
      if (res.verified) {
        Cookies.set("no_2fa", "1", {
          domain: "." + getSiteMainDomain()
        });
        window.location.href =
          window.location.origin + `/?ticket=${ticket}&login_status=true`;
      } else {
        toast.error(t({ defaultMessage: "验证码错误" }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authLogout();
  };

  const countDown = () => {
    setCount(preCount => {
      if (preCount > 0) {
        timer.current = setTimeout(countDown, 1000);
        return --preCount;
      }
      return preCount;
    });
  };

  const handleReSend = async () => {
    try {
      setSending(true);
      await sendEmailVerifyCode({ ticket });
      toast.success(t({ defaultMessage: "发送成功" }));
      setCount(countDuration);
      Promise.resolve().then(countDown);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    countDown();

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return (
    <Container component="main" maxWidth={false} className={classes.container}>
      <Container component="div" maxWidth="xs" className={classes.center}>
        <div className={classes.paper}>
          <img src={nubisIcon} className={classes.logo} />
          <Typography component="h3" variant="body2" color="textSecondary">
            {t({
              defaultMessage: "请输入您email收到的验证码"
            })}
          </Typography>
          <form className={classes.form} onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="email"
              label={t({ defaultMessage: "请输入验证码" })}
              name="email"
              autoComplete="email"
              autoFocus
              helperText={
                isError &&
                t({
                  defaultMessage: "验证码不能为空"
                })
              }
              error={isError}
              value={code}
              onChange={e => setCode(e.target.value)}
            />
            <Typography
              component="h3"
              variant="body2"
              color="textSecondary"
              className={classes.regCode}
            >
              <span>{t({ defaultMessage: "没有收到验证码？" })}</span>
              {count === 0 ? (
                <span
                  className={classes.resend}
                  onClick={handleReSend}
                  style={sending ? { pointerEvents: "none" } : undefined}
                >
                  {t({ defaultMessage: "重新发送" })}
                </span>
              ) : (
                <span> （{count}s）</span>
              )}
            </Typography>
            <Button
              type="submit"
              fullWidth
              size="large"
              variant="contained"
              color="primary"
              className={classes.submit}
              startIcon={loading && <CircularProgress color="#fff" size={16} />}
              style={
                loading
                  ? { cursor: "default", pointerEvents: "none" }
                  : undefined
              }
            >
              {t({ defaultMessage: "登录" })}
            </Button>
            <Button
              type="button"
              fullWidth
              size="large"
              variant="outlined"
              startIcon={loading && <CircularProgress color="#fff" size={16} />}
              style={
                loading
                  ? { cursor: "default", pointerEvents: "none" }
                  : undefined
              }
              onClick={handleLogout}
            >
              {t({ defaultMessage: "切换账号" })}
            </Button>
          </form>
        </div>
        <div className={classes.footer}>
          <Container className={classes.footerSplit}>
            <Divider />
            <Typography
              color="textSecondary"
              className={classes.footerSplitText}
            >
              {t({ defaultMessage: "ORA下载" })}
            </Typography>
          </Container>
          <Grid container justifyContent="space-around">
            <Grid item>
              <Link
                underline="always"
                className={classes.link}
                href={`${APP_ENV.REACT_APP_ORA_WIN_DOWNLOAD_LINK}`}
              >
                <Avatar className={classes.icon}>
                  <IconWindows />
                </Avatar>
                <Typography color="inherit" variant="body2">
                  Windows
                </Typography>
              </Link>
            </Grid>
            <Grid item>
              <Link
                underline="always"
                className={classes.link}
                href={`${APP_ENV.REACT_APP_ORA_ANDROID_DOWNLOAD_LINK}`}
              >
                <Avatar className={classes.icon}>
                  <IconAndroid />
                </Avatar>
                <Typography color="inherit" variant="body2">
                  Android
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </div>
      </Container>
    </Container>
  );
}
